import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { PageHero } from '../components/layout/PageHero';
import { FormStepper } from '../components/supplier/FormStepper';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { UploadIcon, CheckCircleIcon, FileTextIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
import { countries } from '../data/countries';
import { isValidPhoneNumber } from 'libphonenumber-js';
declare global {
  interface Window {
    Forminit: any;
  }
}
const FORMINIT_FORM_ID = 'jrj3prirapz';
const MAX_TOTAL_FILE_SIZE_MB = 25;
type DocKey = 'incorporation' | 'taxClearance' | 'auditedFinancials' | 'companyProfile';
interface DocumentDef {
  key: DocKey;
  label: string;
  required: boolean;
  fieldName: string;
}
const documentDefs: DocumentDef[] = [{
  key: 'incorporation',
  label: 'Certificate of Incorporation',
  required: true,
  fieldName: 'fi-file-incorporation'
}, {
  key: 'taxClearance',
  label: 'Tax Clearance Certificate',
  required: true,
  fieldName: 'fi-file-taxClearance'
}, {
  key: 'auditedFinancials',
  label: 'Audited Financial Statements (Last 2 Years)',
  required: true,
  fieldName: 'fi-file-auditedFinancials'
}, {
  key: 'companyProfile',
  label: 'Company Profile / Brochure',
  required: false,
  fieldName: 'fi-file-companyProfile'
}];
export function SupplierRegistration() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [forminitReady, setForminitReady] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const steps = ['Company Info', 'Business Classification', 'Financial & Capacity', 'Documents', 'Review & Submit'];
  const [formData, setFormData] = useState({
    companyName: '',
    registrationNumber: '',
    country: '',
    address: '',
    city: '',
    postalCode: '',
    contactFullName: '',
    email: '',
    phone: '',
    website: '',
    categories: [] as string[],
    otherCategory: '',
    companyType: '',
    annualTurnover: '',
    employees: '',
    certifications: '',
    confirmAccurate: false,
    agreeCodeOfConduct: false,
    understandFalseInfo: false,
    signature: ''
  });
  const [files, setFiles] = useState<Record<DocKey, File | null>>({
    incorporation: null,
    taxClearance: null,
    auditedFinancials: null,
    companyProfile: null
  });
  const fileInputRefs = {
    incorporation: useRef<HTMLInputElement>(null),
    taxClearance: useRef<HTMLInputElement>(null),
    auditedFinancials: useRef<HTMLInputElement>(null),
    companyProfile: useRef<HTMLInputElement>(null)
  };
  // Load Forminit SDK
  useEffect(() => {
    if (document.getElementById('forminit-sdk')) {
      setForminitReady(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'forminit-sdk';
    script.src = 'https://forminit.com/sdk/v1/forminit.js';
    script.async = true;
    script.onload = () => setForminitReady(true);
    document.body.appendChild(script);
  }, []);
  const categoriesList = ['Food, Beverages & FMCG', 'Supermarket & Retail Goods', 'Wines, Spirits & Beverages', 'Cosmetics, Personal Care & Beauty', 'Pharmaceuticals & Healthcare Products', 'Health & Medical Equipment', 'Electrical & Electronic Components', 'Electrical Installation & Power Equipment', 'Information & Communications Technology', 'Office Supplies, Furniture & Stationery', 'Textiles, Apparel & Uniforms', 'Automotive, Vehicles & Spare Parts', 'Industrial Machinery & Tools', 'Infrastructure & Construction', 'Engineering & Technical Services', 'Agriculture, Agri-Tech & Agro-Processing', 'Energy & Renewable Solutions', 'Water, Sanitation & Environmental Services', 'Logistics, Freight & Transportation', 'Security Services & Equipment', 'Catering, Hospitality & Event Management', 'Cleaning & Facilities Management', 'Printing, Publishing & Branding', 'Education, Training & Capacity Building', 'Consulting & Professional Services', 'Financial, Audit & Legal Services', 'Media, Marketing & Communications', 'Economic Development & Research'];
  // Phone number must be E.164 format AND a real, dialable number per its country.
  const normalizePhone = (raw: string): string => {
    let p = (raw || '').trim().replace(/[\s\-().]/g, '');
    if (p.startsWith('00')) p = '+' + p.slice(2);
    if (!p.startsWith('+') && /^\d{8,}$/.test(p)) p = '+' + p;
    return p;
  };
  const isValidE164 = (phone: string): boolean => {
    // First: must look like E.164 (+ then 8–15 digits)
    if (!/^\+\d{8,15}$/.test(phone)) return false;
    // Then: must be a valid, dialable number for its country (libphonenumber)
    try {
      return isValidPhoneNumber(phone);
    } catch {
      return false;
    }
  };
  const handlePhoneBlur = () => {
    if (!formData.phone) {
      setPhoneError(null);
      return;
    }
    const normalized = normalizePhone(formData.phone);
    if (normalized !== formData.phone) {
      setFormData(prev => ({
        ...prev,
        phone: normalized
      }));
    }
    if (!isValidE164(normalized)) {
      setPhoneError('This does not look like a valid phone number. Please double-check the country code and digits (e.g. +447700900123 for UK, +15550101234 for US, +306900000000 for Greece).');
    } else {
      setPhoneError(null);
    }
  };
  const handleNext = () => {
    if (currentStep === 1) {
      const normalized = normalizePhone(formData.phone);
      if (normalized !== formData.phone) {
        setFormData(prev => ({
          ...prev,
          phone: normalized
        }));
      }
      if (!isValidE164(normalized)) {
        setPhoneError('Please enter a valid, real phone number in international format starting with + and the correct country code (e.g. +447700900123).');
        return;
      }
      setPhoneError(null);
    }
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setSubmitError(null);
    }
  };
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setSubmitError(null);
    }
  };
  const handleFileChange = (key: DocKey, file: File | null) => {
    setFiles(prev => ({
      ...prev,
      [key]: file
    }));
  };
  const totalFileSizeMB = (): number => {
    return Object.values(files).reduce((sum, f) => sum + (f ? f.size : 0), 0) / (1024 * 1024);
  };
  const handleSubmit = async () => {
    setSubmitError(null);
    // Validation: required declarations + signature
    if (!formData.confirmAccurate || !formData.agreeCodeOfConduct || !formData.understandFalseInfo) {
      setSubmitError('Please tick all three declaration checkboxes to proceed.');
      return;
    }
    if (!formData.signature.trim()) {
      setSubmitError('Please type your full name as digital signature.');
      return;
    }
    // Required docs check
    const missingDocs = documentDefs.filter(d => d.required && !files[d.key]);
    if (missingDocs.length > 0) {
      setSubmitError(`Missing required documents: ${missingDocs.map(d => d.label).join(', ')}. Please return to the Documents step.`);
      return;
    }
    // Size check
    if (totalFileSizeMB() > MAX_TOTAL_FILE_SIZE_MB) {
      setSubmitError(`Total file size exceeds ${MAX_TOTAL_FILE_SIZE_MB}MB. Please reduce file sizes and try again.`);
      return;
    }
    if (!forminitReady || !window.Forminit) {
      setSubmitError('Submission service is still loading. Please wait a moment and try again.');
      return;
    }
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      // Phone — re-normalize defensively right before send
      const normalizedPhone = normalizePhone(formData.phone);
      if (!isValidE164(normalizedPhone)) {
        setSubmitError('The phone number you entered does not appear to be valid for the country code. Please go back to Step 1 and correct it (e.g. +447700900123).');
        setIsSubmitting(false);
        return;
      }
      fd.append('fi-sender-fullName', formData.contactFullName);
      fd.append('fi-sender-email', formData.email);
      fd.append('fi-sender-phone', normalizedPhone);
      // Company info
      fd.append('fi-text-companyName', formData.companyName);
      fd.append('fi-text-registrationNumber', formData.registrationNumber);
      fd.append('fi-country-company', formData.country);
      fd.append('fi-text-address', formData.address);
      fd.append('fi-text-city', formData.city);
      fd.append('fi-text-postalCode', formData.postalCode);
      fd.append('fi-text-website', formData.website);
      // Business classification — combine selected categories with custom 'Other' text if provided
      const categoriesPayload = formData.categories.map(c => c === 'Other' && formData.otherCategory.trim() ? `Other: ${formData.otherCategory.trim()}` : c).join(', ');
      fd.append('fi-text-categories', categoriesPayload);
      fd.append('fi-text-companyType', formData.companyType);
      // Financial & capacity
      fd.append('fi-text-annualTurnover', formData.annualTurnover);
      fd.append('fi-text-employees', formData.employees);
      fd.append('fi-text-certifications', formData.certifications);
      // Declarations
      fd.append('fi-text-declarations', [formData.confirmAccurate ? '✓ Confirmed information is accurate' : '', formData.agreeCodeOfConduct ? '✓ Agreed to Supplier Code of Conduct' : '', formData.understandFalseInfo ? '✓ Understood false information consequences' : ''].filter(Boolean).join(' | '));
      fd.append('fi-text-signature', formData.signature);
      // Files
      documentDefs.forEach(d => {
        const f = files[d.key];
        if (f) fd.append(d.fieldName, f);
      });
      const forminit = new window.Forminit();
      const result = await forminit.submit(FORMINIT_FORM_ID, fd);
      if (result && result.error) {
        setSubmitError(result.error.message || 'Submission failed. Please try again.');
        setIsSubmitting(false);
        return;
      }
      setSubmitted(true);
      setIsSubmitting(false);
    } catch (err: any) {
      setSubmitError(err?.message || 'There was a problem submitting your registration. Please try again.');
      setIsSubmitting(false);
    }
  };
  if (submitted) {
    return <div data-id="element-834">
        <PageHero title="Supplier Registration" subtitle="Register your company to participate in AU procurement opportunities" breadcrumbs={[{
        label: 'Home',
        path: '/'
      }, {
        label: 'Procurement'
      }, {
        label: 'Supplier Registration'
      }]} data-id="element-835" />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-id="element-836">
          <Card data-id="element-837">
            <div className="w-20 h-20 bg-au-green-light rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-838">
              <CheckCircleIcon size={48} className="text-au-green" data-id="element-839" />
            </div>
            <h2 className="text-3xl font-serif text-charcoal mb-3" data-id="element-840">
              Registration Submitted
            </h2>
            <div className="w-16 h-1 bg-au-gold mx-auto mb-6" data-id="element-841"></div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-842">
              Thank you for registering with the African Union Commission. Your
              supplier application and supporting documents have been received.
              Our procurement team will review your submission and contact you
              within <strong data-id="element-843">5–10 business days</strong>.
            </p>
            <p className="text-sm text-gray-600 mb-8" data-id="element-844">
              For urgent enquiries, contact{' '}
              <a href="mailto:procurement@aucommission.org" className="text-au-green font-medium hover:underline" data-id="element-845">
                procurement@aucommission.org
              </a>
            </p>
            <Button variant="secondary" onClick={() => {
            window.location.href = '/';
          }} data-id="element-846">
              Return to Home
            </Button>
          </Card>
        </div>
      </div>;
  }
  return <div data-id="element-847">
      <PageHero title="Supplier Registration" subtitle="Register your company to participate in AU procurement opportunities" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Procurement'
    }, {
      label: 'Supplier Registration'
    }]} data-id="element-848" />

      <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-849">
        <FormStepper currentStep={currentStep} steps={steps} data-id="element-850" />

        <Card data-id="element-851">
          {currentStep === 1 && <div data-id="element-852">
              <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-853">
                Company Information
              </h2>
              <div className="space-y-4" data-id="element-854">
                <div className="grid md:grid-cols-2 gap-4" data-id="element-855">
                  <div data-id="element-856">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-857">
                      Company Name <span className="text-au-red" data-id="element-858">*</span>
                    </label>
                    <input type="text" value={formData.companyName} onChange={e => setFormData({
                  ...formData,
                  companyName: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Enter company name" data-id="element-859" />
                  </div>
                  <div data-id="element-860">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-861">
                      Registration Number <span className="text-au-red" data-id="element-862">*</span>
                    </label>
                    <input type="text" value={formData.registrationNumber} onChange={e => setFormData({
                  ...formData,
                  registrationNumber: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Company registration number" data-id="element-863" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4" data-id="element-864">
                  <div data-id="element-865">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-866">
                      Country <span className="text-au-red" data-id="element-867">*</span>
                    </label>
                    <select value={formData.country} onChange={e => setFormData({
                  ...formData,
                  country: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green bg-white" data-id="element-868">
                      <option value="" data-id="element-869">Select country</option>
                      {countries.map(c => <option key={c.code} value={c.code} data-id="element-870">
                          {c.name}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-871">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-872">
                      City <span className="text-au-red" data-id="element-873">*</span>
                    </label>
                    <input type="text" value={formData.city} onChange={e => setFormData({
                  ...formData,
                  city: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="City" data-id="element-874" />
                  </div>
                </div>

                <div data-id="element-875">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-876">
                    Address <span className="text-au-red" data-id="element-877">*</span>
                  </label>
                  <input type="text" value={formData.address} onChange={e => setFormData({
                ...formData,
                address: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Street address" data-id="element-878" />
                </div>

                <div className="grid md:grid-cols-2 gap-4" data-id="element-879">
                  <div data-id="element-880">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-881">
                      Postal Code <span className="text-au-red" data-id="element-882">*</span>
                    </label>
                    <input type="text" value={formData.postalCode} onChange={e => setFormData({
                  ...formData,
                  postalCode: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Postal code" data-id="element-883" />
                  </div>
                  <div data-id="element-884">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-885">
                      Website
                    </label>
                    <input type="url" value={formData.website} onChange={e => setFormData({
                  ...formData,
                  website: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="https://www.example.com" data-id="element-886" />
                  </div>
                </div>

                <div className="border-t border-gray-border pt-6 mt-6" data-id="element-887">
                  <h3 className="font-bold text-charcoal mb-4" data-id="element-888">
                    Primary Contact Person
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4" data-id="element-889">
                    <div data-id="element-890">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-891">
                        Full Name <span className="text-au-red" data-id="element-892">*</span>
                      </label>
                      <input type="text" value={formData.contactFullName} onChange={e => setFormData({
                    ...formData,
                    contactFullName: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Contact person name" data-id="element-893" />
                    </div>
                    <div data-id="element-894">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-895">
                        Email <span className="text-au-red" data-id="element-896">*</span>
                      </label>
                      <input type="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="contact@company.com" data-id="element-897" />
                    </div>
                    <div data-id="element-898">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-899">
                        Phone <span className="text-au-red" data-id="element-900">*</span>
                      </label>
                      <input type="tel" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} onBlur={handlePhoneBlur} className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green ${phoneError ? 'border-au-red' : 'border-gray-border'}`} placeholder="+447700900123" data-id="element-901" />
                      <p className="mt-1 text-xs text-gray-500" data-id="element-902">
                        Use international format starting with + and country
                        code (e.g. +447700900123).
                      </p>
                      {phoneError && <p className="mt-1 text-xs text-au-red" data-id="element-903">{phoneError}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>}

          {currentStep === 2 && <div data-id="element-904">
              <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-905">
                Business Classification
              </h2>
              <div className="space-y-6" data-id="element-906">
                <div data-id="element-907">
                  <label className="block text-sm font-medium text-charcoal mb-3" data-id="element-908">
                    Select Categories / Sectors{' '}
                    <span className="text-au-red" data-id="element-909">*</span>
                  </label>
                  <div className="grid md:grid-cols-2 gap-3" data-id="element-910">
                    {categoriesList.map(cat => <label key={cat} className="flex items-center gap-2 p-3 border border-gray-border rounded-lg hover:bg-au-green-light cursor-pointer transition-colors" data-id="element-911">
                        <input type="checkbox" checked={formData.categories.includes(cat)} onChange={e => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        categories: [...formData.categories, cat]
                      });
                    } else {
                      setFormData({
                        ...formData,
                        categories: formData.categories.filter(c => c !== cat)
                      });
                    }
                  }} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-912" />
                        <span className="text-sm" data-id="element-913">{cat}</span>
                      </label>)}
                    <label className="flex items-center gap-2 p-3 border border-gray-border rounded-lg hover:bg-au-green-light cursor-pointer transition-colors" data-id="element-914">
                      <input type="checkbox" checked={formData.categories.includes('Other')} onChange={e => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        categories: [...formData.categories, 'Other']
                      });
                    } else {
                      setFormData({
                        ...formData,
                        categories: formData.categories.filter(c => c !== 'Other'),
                        otherCategory: ''
                      });
                    }
                  }} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-915" />
                      <span className="text-sm" data-id="element-916">Other (please specify)</span>
                    </label>
                  </div>
                  {formData.categories.includes('Other') && <div className="mt-3" data-id="element-917">
                      <input type="text" value={formData.otherCategory} onChange={e => setFormData({
                  ...formData,
                  otherCategory: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Please describe your category / sector" data-id="element-918" />
                    </div>}
                </div>

                <div data-id="element-919">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-920">
                    Company Type <span className="text-au-red" data-id="element-921">*</span>
                  </label>
                  <select value={formData.companyType} onChange={e => setFormData({
                ...formData,
                companyType: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green bg-white" data-id="element-922">
                    <option value="" data-id="element-923">Select company type</option>
                    <option value="Manufacturer" data-id="element-924">Manufacturer</option>
                    <option value="Distributor" data-id="element-925">Distributor</option>
                    <option value="Service Provider" data-id="element-926">Service Provider</option>
                    <option value="Consultant" data-id="element-927">Consultant</option>
                    <option value="Contractor" data-id="element-928">Contractor</option>
                  </select>
                </div>
              </div>
            </div>}

          {currentStep === 3 && <div data-id="element-929">
              <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-930">
                Financial & Capacity Information
              </h2>
              <div className="space-y-4" data-id="element-931">
                <div className="grid md:grid-cols-2 gap-4" data-id="element-932">
                  <div data-id="element-933">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-934">
                      Annual Turnover (GBP){' '}
                      <span className="text-au-red" data-id="element-935">*</span>
                    </label>
                    <select value={formData.annualTurnover} onChange={e => setFormData({
                  ...formData,
                  annualTurnover: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green bg-white" data-id="element-936">
                      <option value="" data-id="element-937">Select range</option>
                      <option value="£0 – £100,000" data-id="element-938">£0 – £100,000</option>
                      <option value="£100,000 – £500,000" data-id="element-939">
                        £100,000 – £500,000
                      </option>
                      <option value="£500,000 – £1,000,000" data-id="element-940">
                        £500,000 – £1,000,000
                      </option>
                      <option value="£1,000,000 – £5,000,000" data-id="element-941">
                        £1,000,000 – £5,000,000
                      </option>
                      <option value="£5,000,000+" data-id="element-942">£5,000,000+</option>
                    </select>
                  </div>
                  <div data-id="element-943">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-944">
                      Number of Employees <span className="text-au-red" data-id="element-945">*</span>
                    </label>
                    <select value={formData.employees} onChange={e => setFormData({
                  ...formData,
                  employees: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green bg-white" data-id="element-946">
                      <option value="" data-id="element-947">Select range</option>
                      <option value="1-10" data-id="element-948">1-10</option>
                      <option value="11-50" data-id="element-949">11-50</option>
                      <option value="51-200" data-id="element-950">51-200</option>
                      <option value="201-500" data-id="element-951">201-500</option>
                      <option value="500+" data-id="element-952">500+</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-953">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-954">
                    Certifications & Accreditations
                  </label>
                  <textarea value={formData.certifications} onChange={e => setFormData({
                ...formData,
                certifications: e.target.value
              })} rows={4} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="List any relevant certifications (ISO, industry-specific, etc.)" data-id="element-955" />
                </div>
              </div>
            </div>}

          {currentStep === 4 && <div data-id="element-956">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-957">
                Document Uploads
              </h2>
              <p className="text-sm text-gray-600 mb-6" data-id="element-958">
                Maximum total file size: {MAX_TOTAL_FILE_SIZE_MB}MB. Accepted
                formats: PDF, DOC, DOCX, JPG, PNG. Current total:{' '}
                <span className="font-bold text-au-green" data-id="element-959">
                  {totalFileSizeMB().toFixed(2)}MB
                </span>
              </p>
              <div className="space-y-4" data-id="element-960">
                {documentDefs.map(doc => {
              const file = files[doc.key];
              return <div key={doc.key} className={`border-2 border-dashed rounded-lg p-6 transition-colors ${file ? 'border-au-green bg-au-green-light' : 'border-gray-border hover:border-au-green'}`} data-id="element-961">
                      <div className="flex items-center justify-between mb-3" data-id="element-962">
                        <label className="text-sm font-medium text-charcoal" data-id="element-963">
                          {doc.label}{' '}
                          {doc.required && <span className="text-au-red" data-id="element-964">*</span>}
                        </label>
                        {file && <span className="text-xs text-au-green font-medium" data-id="element-965">
                            {(file.size / (1024 * 1024)).toFixed(2)}MB
                          </span>}
                      </div>

                      <input ref={fileInputRefs[doc.key]} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => {
                  const f = e.target.files?.[0] || null;
                  handleFileChange(doc.key, f);
                }} className="hidden" data-id="element-966" />

                      {!file ? <div className="flex items-center gap-4" data-id="element-967">
                          <Button variant="secondary" onClick={() => fileInputRefs[doc.key].current?.click()} className="flex items-center gap-2" data-id="element-968">
                            <UploadIcon size={16} data-id="element-969" />
                            Choose File
                          </Button>
                          <span className="text-sm text-gray-500" data-id="element-970">
                            No file chosen
                          </span>
                        </div> : <div className="flex items-center justify-between bg-white border border-gray-border rounded-lg p-3" data-id="element-971">
                          <div className="flex items-center gap-3 min-w-0" data-id="element-972">
                            <FileTextIcon size={20} className="text-au-green flex-shrink-0" data-id="element-973" />
                            <span className="text-sm text-charcoal truncate" data-id="element-974">
                              {file.name}
                            </span>
                          </div>
                          <button type="button" onClick={() => {
                    handleFileChange(doc.key, null);
                    if (fileInputRefs[doc.key].current) {
                      fileInputRefs[doc.key].current!.value = '';
                    }
                  }} className="text-au-red hover:text-red-700 flex-shrink-0 ml-3" aria-label="Remove file" data-id="element-975">
                            <XCircleIcon size={20} data-id="element-976" />
                          </button>
                        </div>}
                    </div>;
            })}
              </div>

              {totalFileSizeMB() > MAX_TOTAL_FILE_SIZE_MB && <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-977">
                  <AlertCircleIcon size={18} className="flex-shrink-0 mt-0.5" data-id="element-978" />
                  <span data-id="element-979">
                    Total file size exceeds {MAX_TOTAL_FILE_SIZE_MB}MB. Please
                    reduce file sizes before submitting.
                  </span>
                </div>}
            </div>}

          {currentStep === 5 && <div data-id="element-980">
              <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-981">
                Review & Submit
              </h2>
              <div className="space-y-6" data-id="element-982">
                <div className="bg-au-green-light rounded-lg p-6" data-id="element-983">
                  <h3 className="font-bold text-charcoal mb-4" data-id="element-984">
                    Company Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm" data-id="element-985">
                    <div data-id="element-986">
                      <span className="text-gray-600" data-id="element-987">Company Name:</span>
                      <p className="font-medium" data-id="element-988">
                        {formData.companyName || 'Not provided'}
                      </p>
                    </div>
                    <div data-id="element-989">
                      <span className="text-gray-600" data-id="element-990">
                        Registration Number:
                      </span>
                      <p className="font-medium" data-id="element-991">
                        {formData.registrationNumber || 'Not provided'}
                      </p>
                    </div>
                    <div data-id="element-992">
                      <span className="text-gray-600" data-id="element-993">Country:</span>
                      <p className="font-medium" data-id="element-994">
                        {formData.country ? countries.find(c => c.code === formData.country)?.name || formData.country : 'Not provided'}
                      </p>
                    </div>
                    <div data-id="element-995">
                      <span className="text-gray-600" data-id="element-996">Contact Email:</span>
                      <p className="font-medium" data-id="element-997">
                        {formData.email || 'Not provided'}
                      </p>
                    </div>
                    <div data-id="element-998">
                      <span className="text-gray-600" data-id="element-999">Categories:</span>
                      <p className="font-medium" data-id="element-1000">
                        {formData.categories.length > 0 ? formData.categories.join(', ') : 'None selected'}
                      </p>
                    </div>
                    <div data-id="element-1001">
                      <span className="text-gray-600" data-id="element-1002">Documents Attached:</span>
                      <p className="font-medium" data-id="element-1003">
                        {Object.values(files).filter(Boolean).length} of{' '}
                        {documentDefs.length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3" data-id="element-1004">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1005">
                    <input type="checkbox" checked={formData.confirmAccurate} onChange={e => setFormData({
                  ...formData,
                  confirmAccurate: e.target.checked
                })} className="mt-1 w-4 h-4 text-au-green focus:ring-au-green" data-id="element-1006" />
                    <span className="text-sm text-gray-700" data-id="element-1007">
                      I confirm that all information provided is accurate and
                      complete
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1008">
                    <input type="checkbox" checked={formData.agreeCodeOfConduct} onChange={e => setFormData({
                  ...formData,
                  agreeCodeOfConduct: e.target.checked
                })} className="mt-1 w-4 h-4 text-au-green focus:ring-au-green" data-id="element-1009" />
                    <span className="text-sm text-gray-700" data-id="element-1010">
                      I agree to the African Union Supplier Code of Conduct
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1011">
                    <input type="checkbox" checked={formData.understandFalseInfo} onChange={e => setFormData({
                  ...formData,
                  understandFalseInfo: e.target.checked
                })} className="mt-1 w-4 h-4 text-au-green focus:ring-au-green" data-id="element-1012" />
                    <span className="text-sm text-gray-700" data-id="element-1013">
                      I understand that providing false information may result
                      in disqualification
                    </span>
                  </label>
                </div>

                <div className="border-t border-gray-border pt-6" data-id="element-1014">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1015">
                    Digital Signature <span className="text-au-red" data-id="element-1016">*</span>
                  </label>
                  <input type="text" value={formData.signature} onChange={e => setFormData({
                ...formData,
                signature: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Type your full name as signature" data-id="element-1017" />
                </div>

                {submitError && <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-1018">
                    <AlertCircleIcon size={18} className="flex-shrink-0 mt-0.5" data-id="element-1019" />
                    <span data-id="element-1020">{submitError}</span>
                  </div>}
              </div>
            </div>}

          <div className="flex justify-between mt-8 pt-6 border-t border-gray-border" data-id="element-1021">
            <Button variant="secondary" onClick={handlePrevious} disabled={currentStep === 1 || isSubmitting} className="disabled:opacity-50" data-id="element-1022">
              Previous
            </Button>
            {currentStep < steps.length ? <Button variant="primary" onClick={handleNext} data-id="element-1023">
                Next Step
              </Button> : <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting} className="flex items-center gap-2 disabled:opacity-60" data-id="element-1024">
                {isSubmitting ? <>Submitting...</> : <>
                    <CheckCircleIcon size={20} data-id="element-1025" />
                    Submit Registration
                  </>}
              </Button>}
          </div>
        </Card>
      </div>
    </div>;
}